import React, { useEffect, useMemo, useState } from 'react';
import escortIcon from '../../assets/icons/ic_escort.svg';
import IconTint from 'react-icon-tint';
import Dropdown from '../Dropdown';
import { useAtom } from 'jotai';
import { currentRobot, escortFrom, escortName } from '../store/DataStore';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { buildTask } from '../../lib/robotCommands';
import { enqueueTask } from '../../lib/enqueueTask';
import { getSortedVisibleLocations, getVisibleLocationOrDefault } from './locationVisibility';

const EscortCheckedinPopup = ({ isOpen, children, onClose, onSend, locations, currentLocation, escortNameFromCheckedIn, escortLocationFromCheckedIn, escortLocationToCheckedIn, batteryAmount, userName, savedNickname }) => {
    const [show, setShow] = useState(isOpen);
    const [selectedFromSaved, setSelectedFromSaved] = useAtom(escortFrom);
    const [selectedToSaved, setSelectedToSaved] = useState('');
    const [selectedName, setSelectedName] = useAtom(escortName);
    const [isDropdownTouched, setIsDropdownTouched] = useState(false);
    const [savedRobot] = useAtom(currentRobot);
    const sortedLocations = useMemo(() => getSortedVisibleLocations(locations), [locations]);
    const defaultCheckedInLocation = getVisibleLocationOrDefault(escortLocationToCheckedIn, sortedLocations);

    useEffect(() => {
        if (isOpen) {
            console.log("My current location is " + currentLocation);
            setShow(true);
            if (!isDropdownTouched) {
                setSelectedFromSaved(defaultCheckedInLocation);
                setSelectedToSaved(defaultCheckedInLocation);
                setSelectedName(escortNameFromCheckedIn);
            }
        } else {
            setShow(false);
            setTimeout(onClose, 200);
        }
    }, [isOpen, onClose, currentLocation, escortLocationToCheckedIn, escortNameFromCheckedIn, isDropdownTouched, defaultCheckedInLocation, setSelectedFromSaved, setSelectedToSaved, setSelectedName]);
    

    const setIsCurrent = async (isCurrent) => {
        const docPath = `Companies/Zeeno_Dev/Settings/${savedRobot}/Controller/actions`;
        const docRef = doc(db, docPath);

        try {
            await updateDoc(docRef, {
                is_current: isCurrent,
            });
        } catch (error) {
            console.error('error updated location: ', error);
        }
    };

    const handleClose = () => {
        setShow(false);
        setTimeout(onClose, 200);
        setSelectedFromSaved(sortedLocations[0] || '');
        setSelectedToSaved(sortedLocations[0] || '');
        setSelectedName('');
        setIsDropdownTouched(false);
    };

    const handleSend = async () => {
        onSend();
        setShow(false);
        setTimeout(onClose, 200);
        setSelectedFromSaved(getVisibleLocationOrDefault(currentLocation, sortedLocations));
        setSelectedToSaved(sortedLocations[0] || '');
        setSelectedName('');

        const newDocRef = await enqueueTask(db, savedRobot, buildTask('escort', {
            name: selectedName,
            from: selectedFromSaved,
            to: selectedToSaved,
        }, { userName, robotName: savedNickname }));
        console.log('Document successfully updated with ID: ', newDocRef.id);

        console.log("Selected from saved is " + selectedFromSaved);
        if (selectedFromSaved === currentLocation) {
            setIsCurrent(true);
        } else {
            setIsCurrent(false);
        }
    };

    const setSelectedFrom = (item) => {
        setIsDropdownTouched(true);
        setSelectedFromSaved(item);
    };

    const setSelectedTo = (item) => {
        setIsDropdownTouched(true);
        setSelectedToSaved(item);
    };

    const canSend = selectedName !== '' &&
        selectedFromSaved !== selectedToSaved &&
        sortedLocations.includes(selectedFromSaved) &&
        sortedLocations.includes(selectedToSaved);

    return (
        <div
            className={`fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-[full] transition-opacity duration-200 ${show ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleClose}
            style={{ display: show || isOpen ? 'block' : 'none' }}
        >
            <div
                className="relative top-20 mx-auto p-4 border w-fit shadow-lg rounded-md bg-white ml"
                onClick={e => e.stopPropagation()}
            >
                <div className="absolute top-4 right-4">
                    <button onClick={handleClose} className="text-black bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg p-0.5 text-sm inline-flex items-center">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <h2 className="text-lg font-medium leading-6 text-[#3C5BD2] font-heebo mt-6">Checked In Patients</h2>
                <p className="text-md text-gray-800 font-heebo font-light mt-1">{`Please set ${savedNickname} to escort patient`}</p>

                <div className="flex flex-col items-center mt-8">
                    {children}
                </div>
                <input className='bg-[#3C5BD2] bg-opacity-10 p-4 h-10 mt-4 rounded-lg w-full outline-none font-heebo font-light' placeholder='Type Name...' value={selectedName} onChange={(e) => setSelectedName(e.target.value)} />
                <div className='flex flex-row mt-3 gap-4'>
                    <div>
                        <span className='font-heebo text-md font-light'>From</span>
                        {sortedLocations.length > 0 ? (
                            <Dropdown list={sortedLocations} setSelected={(item) => setSelectedFrom(item)} selectedItem={selectedFromSaved} dropdownWidth={"250"} />
                        ) : (
                            <div className="mt-1 w-[250px] rounded-md bg-[#3C5BD2] bg-opacity-10 px-3 py-2 font-heebo font-light text-gray-600">
                                No locations available
                            </div>
                        )}
                    </div>
                    <div>
                        <span className='font-heebo text-md font-light'>to</span>
                        {sortedLocations.length > 0 ? (
                            <Dropdown list={sortedLocations} setSelected={(item) => setSelectedTo(item)} selectedItem={selectedToSaved} dropdownWidth={"250"} />
                        ) : (
                            <div className="mt-1 w-[250px] rounded-md bg-[#3C5BD2] bg-opacity-10 px-3 py-2 font-heebo font-light text-gray-600">
                                No locations available
                            </div>
                        )}
                    </div>
                </div>
                <div className='w-full flex justify-center mt-14'>
                    {canSend ?
                        <div className='flex w-fit bg-[#152FF5] py-1.5 px-5 rounded-3xl items-center cursor-pointer' onClick={handleSend}>
                            <span className='text-white font-heebo'>Confirm Escort</span>
                            <IconTint src={escortIcon} className='h-5 ml-2' color='#FFFFFF' />
                        </div>
                        :
                        <div className='flex w-fit bg-[#152FF5] py-1.5 px-5 rounded-3xl items-center opacity-50'>
                            <span className='text-white font-heebo'>Confirm Escort</span>
                            <IconTint src={escortIcon} className='h-5 ml-2' color='#FFFFFF' />
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default EscortCheckedinPopup;
